import { useContext, useMemo } from "react";

import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { AcknowledgementRequirement } from "./acknowledgements";
import { computeDispatchItems, type DispatchItem, dispatchItemCollections } from "./dispatchItems";
import type { TaskFold, TasksElsewhere } from "./tasks";
import { useMessageContentDownload } from "./useMessageContentDownload";

/** Stable fallback while the compute's first run is pending. */
const NO_ITEMS: DispatchItem[] = [];

/**
 * The items a message-grain Dispatch view lists, recomputed as the store changes.
 *
 * The driver's messages and TMS events, each when the view's Requires Acknowledgement list takes its kind, handled
 * while an open task in `tasks` names them, and the tasks in `elsewhere` when the list takes them. Off (an empty,
 * stable list) at the driver grain, so a driver-per-row view never pays for walking every item.
 *
 * The warmup loads items without their content, so a message row shown here downloads its own text and files once;
 * the row renders without text until they land.
 */
export function useDispatchItemRows(
  workspaceId: string,
  membershipId: string | undefined,
  grain: DispatchGrain,
  teamAliases: readonly string[],
  requires: AcknowledgementRequirement,
  tasks: TaskFold,
  elsewhere: TasksElsewhere,
): DispatchItem[] {
  const { myAccount } = useContext(MyAccountContext);
  const language = myAccount?.preferredLanguage ?? "en";
  const enabled = grain === "message";
  const teamAliasesKey = teamAliases.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: the joined key is the aliases' identity
  const computed = useCollectionsCompute(
    dispatchItemCollections,
    () =>
      enabled && workspaceId && membershipId
        ? computeDispatchItems({
            workspaceId,
            membershipId,
            language,
            teamAliases,
            requires,
            tasks,
            elsewhere,
          })
        : null,
    [enabled, workspaceId, membershipId, language, teamAliasesKey, requires, tasks, elsewhere],
    "Dispatch:items",
  );
  const rows = useMemo(() => computed ?? NO_ITEMS, [computed]);

  // Each message row downloads its own text.
  const messageIdsKey = useMemo(
    () =>
      rows
        .filter((row) => row.source === "message")
        .map((row) => row.id)
        .join(","),
    [rows],
  );
  useMessageContentDownload(workspaceId, messageIdsKey, language, { what: "Message" });

  return rows;
}
